export interface CustomerErrorRes {
  service: string;
  statusCode: number;
  request: string;
  errorMessage: string;
}

/**
 * Meant to check for errors found from services called within lambda.
 *
 * The `Error` field returned by lambdas is not always a `CustomerErrorRes[]`. When an upstream
 * service fails (e.g. "SSI API returned status 500") the lambda can populate it with a plain
 * string or object. Guard against non-array values so we never call `.forEach` on a non-array,
 * which previously crashed the whole page with "e.forEach is not a function".
 *
 * @param errors: CustomerErrorRes[] | string | unknown
 * @returns boolean
 */
export const lambdaErrorCheck = (errors: CustomerErrorRes[] | string | null | undefined) => {
  if (!errors) return false;

  if (!Array.isArray(errors)) {
    printLambdaMessage(errors);
    return true;
  }

  if (errors.length === 0) return false;
  errors.forEach((error) => printLambdaError(error));
  return true;
};

/**
 * Only print non-array lambda error payloads (e.g. a plain string message) in PreProd environments.
 */
const printLambdaMessage = (error: unknown) => {
  const preProdEnv = /(localhost|test|qa|stage)/.test(window.location.hostname);
  if (preProdEnv) console.error("ERROR RETURNED FROM LAMBDA:", { error });
};

/**
 * Only print errors found from lambdas in PreProd environments.
 *
 * @param errors
 */
const printLambdaError = (error: CustomerErrorRes) => {
  const preProdEnv = /(localhost|test|qa|stage)/.test(window.location.hostname);
  if (preProdEnv) console.error(`${error.statusCode} ERROR RETURNED FROM LAMBDA: (${error.service})`, { error });
};
